import { DeleteTwoTone, HomeTwoTone } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link as MLink,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { saveSalesOrderBulk } from '../api';
import { ContractDialog } from '../components/ContractDialog';
import { isTourBookable, OnlineTourCard } from '../components/OnlineTourCard';
import PageHeader from '../components/PageHeader';
import {
  PurchaseSummary,
  PurchaseSummarySkeleton,
} from '../components/PurchaseSummary';
import { CartItem } from '../context/CartContext';
import { useAccountWithGuard } from '../hooks/api/account-with-guard';
import { useTourPrices } from '../hooks/api/tour-prices';
import { useTourSearchDetail } from '../hooks/api/tour-search';
import { useAuth } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';
import useConfirm from '../hooks/useConfirm';
import { calculatePrice, groupPricesWithAgeGroups } from '../utils';
import {
  IOnlineTourPriceResult,
  IOnlineTourSearchResult,
  ISalesOrderInput,
} from '../utils/types';
import { TourPurchaseGuestsInfo } from './TourPurchase';

export default function CartPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tourData, setTourData] = useState<IOnlineTourSearchResult>();
  const { t } = useTranslation();
  const { cart, removeFromCart, resetCart } = useCart();
  const { token } = useAuth();
  const nav = useNavigate();
  const confirm = useConfirm();
  // redirect to login if not authenticated
  useAccountWithGuard();
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const showContract = () => {
    setContractDialogOpen(true);
  };

  const items = useMemo(() => {
    const items: ISalesOrderInput[] = [];

    function addItem(priceId: number, a: TourPurchaseGuestsInfo['adults'][0]) {
      if (
        !priceId ||
        !a.id ||
        !a.contact ||
        !a.contact.address.id ||
        !a.contact.email.id ||
        !a.contact.phone?.id
      ) {
        throw new Error('Invalid data');
      }
      items.push({
        partyId: a.id,
        addressId: a.contact.address.id,
        electronicAddressId: a.contact.email.id,
        phoneNumberId: a.contact.phone?.id,
        pricingId: priceId,
      });
    }

    for (const it of cart.items) {
      for (const a of it.guests.adults) {
        try {
          addItem(it.priceId as number, a);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          setError(e.message);
          return [];
        }
      }
      for (const c of it.guests.children) {
        try {
          addItem(c.pricingId as number, c);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          setError(e.message);
          return [];
        }
      }
    }
    return items;
  }, [cart.items]);

  if (!token) {
    nav('/login?next=/cart');
    return null;
  }

  async function handleCheckout() {
    setLoading(true);
    setError(null);
    if (!cart.tourId) return;
    if (items.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const res = await saveSalesOrderBulk(token, cart.tourId, items);
      resetCart();
      nav(`/order-result?pnr=${res[0].salesGroup.code}`, {
        state: res,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ContractLink = (props: any) => (
    <MLink
      color="primary"
      underline="always"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        showContract();
      }}
      {...props}
    />
  );

  return (
    <Box maxWidth={1440} marginX="auto" sx={{ pt: 2 }}>
      <PageHeader title={t('cartPage.title')} />
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <Link
          to="/"
          style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </Link>
        {cart.tourId && (
          <Link to="/tours" style={{ color: 'inherit' }}>
            <Typography>{t('cartPage.tours')}</Typography>
          </Link>
        )}
        {cart.tourId && (
          <Link to={`/tours/${cart.tourId}`} style={{ color: 'inherit' }}>
            <Typography>#{cart.tourId}</Typography>
          </Link>
        )}
        {cart.tourId && (
          <Link
            to={`/tours/${cart.tourId}/purchase`}
            style={{ color: 'inherit' }}
          >
            <Typography>{t('cartPage.purchase')}</Typography>
          </Link>
        )}
        <Typography>{t('cartPage.title')}</Typography>
      </Breadcrumbs>

      {!cart.tourId ? (
        <Alert severity="info">{t('cartPage.emptyCart')}</Alert>
      ) : (
        <Box>
          <CartTour tourId={cart.tourId} onLoad={setTourData} />

          {cart.items.map((item, i) => (
            <Box key={item.id} sx={{ position: 'relative', mt: 1 }}>
              <CartItemComponent tourId={cart.tourId as number} item={item} />
              <IconButton
                onClick={() =>
                  confirm(
                    t('cartPage.confirmDelete'),
                    t('cartPage.confirmDeleteDescription'),
                  ).then((ok) => {
                    if (!ok) return;
                    setError(null);
                    removeFromCart(item.id);
                  })
                }
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <DeleteTwoTone />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {contractDialogOpen && (
        <ContractDialog
          open={contractDialogOpen}
          onClose={(accepted) => {
            setContractDialogOpen(false);
            setContractAccepted(!!accepted);
          }}
          tourId={cart.tourId as number}
          data={items}
        />
      )}

      {tourData &&
        !isTourBookable(tourData.remainingDay, tourData.availableCapacity) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('cartPage.checkoutDisabledTooltip')}
          </Alert>
        )}

      {cart.tourId && tourData && (
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={contractAccepted}
              onChange={(_e, v) => setContractAccepted(v)}
            />
          }
          label={
            <Trans
              i18nKey="cartPage.contractCheckboxLabel"
              components={{
                ContractLink: <ContractLink />,
              }}
            />
          }
        />
      )}

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        sx={{ mt: 2 }}
        spacing={1}
      >
        {cart.items.length > 0 && (
          <Button
            variant="text"
            color="inherit"
            onClick={() =>
              confirm(
                t('cartPage.confirmClear'),
                t('cartPage.confirmClearDescription'),
              ).then((ok) => {
                if (!ok) return;
                resetCart();
                setError(null);
              })
            }
          >
            {t('cartPage.clearCart')}
          </Button>
        )}
        <Box sx={{ flex: 1, display: ['none', 'block'] }} />
        <Button
          component={Link}
          to={cart.tourId ? `/tours/${cart.tourId}` : '/home'}
          variant={!cart.tourId ? 'contained' : 'text'}
          color="primary"
        >
          {t('cartPage.continueShopping')}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{ ml: 1 }}
          disabled={
            !contractAccepted ||
            !cart.tourId ||
            !tourData ||
            !isTourBookable(tourData.remainingDay, tourData.availableCapacity)
          }
          onClick={() => {
            confirm(
              t('cartPage.confirmCheckout'),
              t('cartPage.confirmCheckoutDescription'),
            ).then((ok) => {
              if (ok) handleCheckout();
            });
          }}
          loading={loading}
        >
          {t('cartPage.checkout')}
        </LoadingButton>
      </Stack>
    </Box>
  );
}

function CartTour(props: {
  tourId: number;
  onLoad: (data?: IOnlineTourSearchResult) => void;
}) {
  const { data, isLoading } = useTourSearchDetail({ id: props.tourId });

  useEffect(() => {
    props.onLoad(data);
  }, [data, props]);

  if (!data) return null;
  if (isLoading) return <LinearProgress />;

  return (
    <OnlineTourCard
      tourId={data.id}
      tourName={data.name}
      tourCode={data.code}
      start={{
        departureCity: data.departureDepartureCity,
        departureAirport: data.departureDeparture,
        arrivalCity: data.arrivalDepartureCity,
        arrivalAirport: data.arrivalDeparture,
      }}
      end={{
        departureCity: data.arrivalDepartureCity,
        departureAirport: data.arrivalDeparture,
        arrivalCity: data.arrivalArrivalCity,
        arrivalAirport: data.arrivalArrival,
      }}
      tourStartDate={data.departureDate}
      tourFinishDate={data.arrivalDate}
      night={data.dayCount - 1}
      day={data.dayCount}
      currencyCode={data.currencyCode}
      remainingTime={data.remainingDay}
      remainingSeat={null}
      coverImg={data.images[0]?.path}
      disableActions
      hideShareAndPrint
      hideCoverImage
    />
  );
}

function CartItemComponent({
  tourId,
  item,
}: {
  tourId: number;
  item: CartItem;
}) {
  const { data: prices, isLoading } = useTourPrices({
    tourId,
  });

  const { t } = useTranslation();

  if (isLoading) return <PurchaseSummarySkeleton />;

  const adultPrices = groupPricesWithAgeGroups(prices || []);
  const selectedPrice = adultPrices?.find((p) => p.price.id === item.priceId);
  const selection = selectedPrice
    ? calculatePrice(item, selectedPrice, { allowSharedRooming: true })
    : null;

  return (
    <Box key={item.id} sx={{ flex: 1 }}>
      {selection ? (
        <PurchaseSummary
          totalPrice={selection.adultPrice + selection.childrenPrice}
          guests={item}
          guestsInfo={item.guests}
          adultPrice={selection.adultPricePerPerson}
          childrenPrice={selection.childrenPrice}
          childrenPrices={selection.childrenPrices as IOnlineTourPriceResult[]}
          currencyCode={selection.price.price.currencyCode}
          room={selection.price.price.bedType}
        />
      ) : (
        <Alert severity="error">
          {t('cartItemComponent.errorPriceNotFound')}
        </Alert>
      )}
    </Box>
  );
}
